import React, { useState, useEffect } from 'react';
import './InputForm.css';

const InputForm = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    placename: 'New York, New York, US',
    birthDate: '1990-01-15',
    birthTime: '10:30',
    city: 'New York',
    state: 'New York',
    county: '',
    country: 'United States',
    latitude: 40.68295,
    longitude: -73.9708
  });
  
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Auto-detect user's location and time on component mount
  useEffect(() => {
    detectUserLocationAndTime();
  }, []);

  // Detect user's location via IP and get local time/timezone
  const detectUserLocationAndTime = async () => {
    try {
      // Fetch user's IP and location info
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) throw new Error('Failed to fetch location');
      
      const locationData = await response.json();
      console.log('Detected location:', locationData);

      // Extract location information
      const city = locationData.city || 'Unknown';
      const state = locationData.region || locationData.region_code || '';
      const country = locationData.country_name || locationData.country || '';
      const latitude = locationData.latitude || 0;
      const longitude = locationData.longitude || 0;
      const timezone = locationData.timezone || 'UTC';

      // Format placename
      const placename = state 
        ? `${city}, ${state}, ${locationData.country_code || ''}`
        : `${city}, ${locationData.country_code || ''}`;

      // Get current local time
      const now = new Date();
      const localTime = new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timezone
      }).format(now);

      // Get today's date in YYYY-MM-DD format
      const today = new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: timezone
      }).format(now);

      // Update form with detected values
      setFormData(prev => ({
        ...prev,
        placename: placename,
        city: city,
        state: state,
        country: country,
        latitude: latitude,
        longitude: longitude,
        birthDate: today,
        birthTime: localTime
      }));

      console.log('Auto-detected values:', {
        placename,
        city,
        state,
        country,
        latitude,
        longitude,
        birthDate: today,
        birthTime: localTime,
        timezone
      });
    } catch (error) {
      console.error('Error detecting location:', error);
      // Keep default values if detection fails
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Fetch suggestions for place name
    if (name === 'placename' && value.length > 1) {
      // Call async function without await - it handles state internally
      fetchPlaceSuggestions(value);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const fetchPlaceSuggestions = async (input) => {
    if (!input || input.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY;
    
    console.log('API Key present:', !!GEOAPIFY_API_KEY);
    console.log('Searching for:', input);
    
    // If no API key is configured, use fallback
    if (!GEOAPIFY_API_KEY) {
      console.log('No API key configured, using fallback city list');
      const fallbackSuggestions = getIndianCities(input);
      setSuggestions(fallbackSuggestions);
      setShowSuggestions(true);
      return;
    }
    
    try {
      // Try Geoapify API first
      const apiUrl = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(input)}&limit=8&apiKey=${GEOAPIFY_API_KEY}`;
                      
      console.log('Calling Geoapify API...');
      
      const response = await fetch(apiUrl);
      console.log('Geoapify response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Geoapify response data:', data);
        
        // Geoapify returns data in 'features' array, not 'results'
        if (data.features && data.features.length > 0) {
          const geoapifySuggestions = data.features.map((feature) => {
            const props = feature.properties;
            
            // Extract city, state, county, and country code from properties
            const city = props.city || props.address_line1 || '';
            const state = props.state || '';
            const county = props.county || '';
            const countryCode = props.country_code ? props.country_code.toUpperCase() : '';
            const country = props.country || '';
            
            // Format: "New York, NY, US" or "New York, Otter Tail County, MN, US"
            let displayText = city;
            if (state) displayText += `, ${state}`;
            if (countryCode) displayText += `, ${countryCode}`;
            
            return {
              description: displayText,
              place_id: props.place_id,
              main_text: city,
              state: state,
              county: county,
              country: country,
              secondary_text: countryCode,
              formatted: props.formatted,
              lat: feature.geometry.coordinates[1],
              lon: feature.geometry.coordinates[0]
            };
          });
          
          console.log('Setting Geoapify suggestions:', geoapifySuggestions);
          setSuggestions(geoapifySuggestions);
          setShowSuggestions(true);
          return;
        } else {
          console.log('No features from Geoapify, using fallback');
        }
      } else {
        console.warn('Geoapify API returned error status:', response.status);
      }
    } catch (error) {
      console.error('Geoapify API error:', error);
    }
    
    // Fallback to Indian cities if Geoapify fails
    console.log('Using fallback city list');
    const fallbackSuggestions = getIndianCities(input);
    setSuggestions(fallbackSuggestions);
    setShowSuggestions(true);
  };

  // Fallback: Predefined list of Indian cities and towns
  const getIndianCities = (input) => {
    const cities = [
      'Erode, India',
      'Chennai, India',
      'Mumbai, India',
      'Delhi, India',
      'Bangalore, India',
      'Hyderabad, India',
      'Kolkata, India',
      'Pune, India',
      'Ahmedabad, India',
      'Jaipur, India',
      'Lucknow, India',
      'Chandigarh, India',
      'Indore, India',
      'Bhopal, India',
      'Visakhapatnam, India',
      'Surat, India',
      'Vadodara, India',
      'Goa, India',
      'Kochi, India',
      'Coimbatore, India',
      'Salem, India',
      'Madurai, India',
      'Tirupati, India',
      'Mysore, India',
      'Nashik, India',
      'Aurangabad, India',
      'Agra, India',
      'Varanasi, India',
      'Allahabad, India',
      'Patna, India'
    ];

    const lowerInput = input.toLowerCase();
    return cities
      .filter(city => city.toLowerCase().includes(lowerInput))
      .map((city, index) => ({
        description: city,
        place_id: `fallback_${index}`,
        main_text: city.split(',')[0],
        secondary_text: city.split(',')[1]?.trim()
      }));
  };

  const handleSelectSuggestion = (suggestion) => {
    const placeName = suggestion.description || 
                     `${suggestion.main_text}, ${suggestion.secondary_text}`;
    setFormData(prev => ({
      ...prev,
      placename: placeName,
      city: suggestion.main_text || '',
      state: suggestion.state || '',
      county: suggestion.county || '',
      country: suggestion.country || '',
      latitude: suggestion.lat || null,
      longitude: suggestion.lon || null
    }));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(
      formData.placename, 
      formData.birthDate, 
      formData.birthTime, 
      formData.latitude, 
      formData.longitude,
      formData.city,
      formData.country
    );
  };

  return (
    <form className="input-form" onSubmit={handleSubmit}>
      <h2>Horoscope Chart Generator</h2>
      
      <div className="form-group">
        <label htmlFor="placename">Place Name:</label>
        <div className="place-input-wrapper">
          <input
            type="text"
            id="placename"
            name="placename"
            value={formData.placename}
            onChange={handleChange}
            onFocus={() => formData.placename.length > 1 && fetchPlaceSuggestions(formData.placename)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="e.g., Erode, India"
            autoComplete="off"
            required
          />
          {showSuggestions && suggestions.length > 0 && (
            <div className="suggestions-dropdown">
              {suggestions.slice(0, 8).map((suggestion, index) => (
                <div
                  key={index}
                  className="suggestion-item"
                  onClick={() => handleSelectSuggestion(suggestion)}
                >
                  <div className="suggestion-main">
                    {suggestion.description || `${suggestion.main_text}, ${suggestion.secondary_text}`}
                  </div>
                  <div className="suggestion-secondary">
                    {suggestion.description && <span>📍 {suggestion.description}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="birthDate">Birth Date:</label>
        <input
          type="date"
          id="birthDate"
          name="birthDate"
          value={formData.birthDate}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="birthTime">Birth Time:</label>
        <input
          type="time"
          id="birthTime"
          name="birthTime"
          value={formData.birthTime}
          onChange={handleChange}
          required
        />
      </div>

      <button type="submit" disabled={isLoading} className="submit-btn">
        {isLoading ? 'Loading...' : 'Generate Chart'}
      </button>
    </form>
  );
};

export default InputForm;